import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getFeatureToggles, 
  updateFeatureToggle, 
  subscribeToFeatureToggles 
} from '../services/featureToggleService';
import { useFirebaseAuth } from './FirebaseAuthContext';
import { debugLog, debugWarn } from '../utils/debug';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Get admin status from Firebase auth instead of localStorage
  const { user, isAdmin: firebaseIsAdmin, adminStatusLoading } = useFirebaseAuth();
  
  // Compute isAdmin from Firebase auth
  const isAuthenticated = !!user;
  const isAdmin = firebaseIsAdmin();
  const isLoading = adminStatusLoading;
  
  // Feature toggles - initialize from cloud
  const [showQualifierLink, setShowQualifierLink] = useState(false);
  const [showAdminLink, setShowAdminLink] = useState(false);
  const [togglesLoading, setTogglesLoading] = useState(true);

  // Load feature toggles immediately for all users
  useEffect(() => {
    let unsubscribe = null;
    
    const loadFeatureToggles = async () => {
      try {
        debugLog('🚀 Loading feature toggles for all users...');
        debugLog('📱 Device info:', {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          onLine: navigator.onLine
        });
        
        const toggles = await getFeatureToggles();
        debugLog('📊 Feature toggles received:', toggles);
        debugLog('🔍 Setting showQualifierLink to:', toggles.showQualifierLink);
        debugLog('🔍 Setting showAdminLink to:', toggles.showAdminLink);
        
        setShowQualifierLink(toggles.showQualifierLink);
        setShowAdminLink(toggles.showAdminLink);
        
        // Subscribe to real-time updates
        unsubscribe = subscribeToFeatureToggles((toggles) => {
          debugLog('🔄 Feature toggle update received:', toggles);
          debugLog('🔄 Updating showQualifierLink to:', toggles.showQualifierLink);
          debugLog('🔄 Updating showAdminLink to:', toggles.showAdminLink);
          setShowQualifierLink(toggles.showQualifierLink);
          setShowAdminLink(toggles.showAdminLink);
        });
        
        debugLog('✅ Feature toggles loaded and subscription active');
      } catch (error) {
        debugWarn("Using default feature toggles due to Firestore error:", error);
        setShowQualifierLink(false);
        setShowAdminLink(false);
      } finally {
        setTogglesLoading(false);
        debugLog('🏁 Feature toggle loading complete');
      }
    };
    
    // Load feature toggles immediately
    loadFeatureToggles();
    
    // Cleanup subscription on unmount
    return () => {
      if (unsubscribe) {
        debugLog('🧹 Cleaning up feature toggle subscription');
        unsubscribe();
      }
    };
  }, []);

  // Clean up any old localStorage admin token on mount
  useEffect(() => {
    const oldToken = localStorage.getItem('adminToken');
    if (oldToken) {
      console.log('🧹 Removing legacy adminToken from localStorage');
      localStorage.removeItem('adminToken');
    }
  }, []);

  // Legacy login/logout functions - kept for backwards compatibility but deprecated
  const login = () => {
    console.warn('Legacy admin login is deprecated. Please use Firebase authentication.');
    return false;
  };

  const logout = () => {
    console.warn('Legacy admin logout is deprecated. Please use Firebase signOut.');
  };

  const toggleQualifierLink = async () => {
    try {
      const newValue = !showQualifierLink;
      console.log('🔄 Toggling qualifier link to:', newValue);
      
      // Don't update local state immediately - let Firestore update it
      const success = await updateFeatureToggle('showQualifierLink', newValue);
      if (success) {
        console.log('✅ Toggle update sent to Firestore, waiting for real-time update...');
        // The real-time listener will update the state
      } else {
        console.error('❌ Failed to update Firestore');
      }
    } catch (error) {
      console.error('Error updating qualifier link toggle:', error);
    }
  };

  const toggleAdminLink = async () => {
    try {
      const newValue = !showAdminLink;
      console.log('🔄 Toggling admin link to:', newValue);
      
      // Don't update local state immediately - let Firestore update it
      const success = await updateFeatureToggle('showAdminLink', newValue);
      if (success) {
        console.log('✅ Toggle update sent to Firestore, waiting for real-time update...');
        // The real-time listener will update the state
      } else {
        console.error('❌ Failed to update Firestore');
      }
    } catch (error) {
      console.error('Error updating admin link toggle:', error);
    }
  };

  const refreshFeatureToggles = async () => {
    try {
      console.log('🔄 Manually refreshing feature toggles...');
      
      // Force clear any cached state
      setShowQualifierLink(false);
      setShowAdminLink(false);
      
      // Load fresh from Firestore
      const toggles = await getFeatureToggles();
      setShowQualifierLink(toggles.showQualifierLink);
      setShowAdminLink(toggles.showAdminLink);
      
      console.log('✅ Feature toggles refreshed from Firestore:', toggles);
      console.log('🔄 Local state updated to:', { 
        showQualifierLink: toggles.showQualifierLink, 
        showAdminLink: toggles.showAdminLink 
      });
    } catch (error) {
      console.error('Error refreshing feature toggles:', error);
    }
  };

  const debugFeatureToggles = () => {
    console.log('🔍 Current Feature Toggle State:');
    console.log('  - showQualifierLink:', showQualifierLink);
    console.log('  - showAdminLink:', showAdminLink);
    console.log('  - togglesLoading:', togglesLoading);
    console.log('  - isLoading:', isLoading);
  };

  const value = {
    isAuthenticated,
    isAdmin,
    isLoading,
    togglesLoading,
    login,
    logout,
    showQualifierLink,
    showAdminLink,
    toggleQualifierLink,
    toggleAdminLink,
    refreshFeatureToggles,
    debugFeatureToggles
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
